// stores/useCaseCreator.js
import { cloneDeep, set as lodashSet } from 'lodash';
import { toast } from 'react-hot-toast';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import api from '../config/axiosConfig';

export const useCaseCreator = create(
  devtools(
    persist(
      (set, get) => ({
        // ============================================================================
        // MODE INTERFACE & CONFIGURATION
        // ============================================================================
        
        interfaceMode: 'simplifie', // 'simplifie' | 'avance'
        language: 'fr',
        
        // ============================================================================
        // ÉTAT FORMULAIRE (DONNÉES MÉTIER)
        // ============================================================================
        
        formData: { 
            slug: `sim-${Date.now().toString(36)}`,
            title: "Nouvelle Simulation",
            name: "Case",
            description: "Description à compléter",
            pedagogicalGoal: "Objectif pédagogique de la simulation",
            tags: ["nouveau", "test"],
            thumbnail: "",
            difficulty: "moyen",
            estimatedTime: 25,
            isPublished: false,
            isLegacy: false,
            version: 2,

            // STEPS : 7 étapes obligatoires + sous-champs minimum
            steps: Array.from({ length: 7 }, (_, i) => ({
                title: [
                "Diagnostic Initial",
                "Montée des Tensions",
                "Point d'Intervention",
                "Bascule Critique",
                "Instabilité Maximale",
                "Résolution / Stabilisation",
                "Bilan Système & Débrief"
                ][i],
                description: "Phase à compléter",
                problematisation: "",
                epistemologicalQuestion: "",
                feedback: "",
                narrativeOverride: "",
                causality: [],
                phases: [
                { type: "observation", duration: 15, description: "Observation initiale" }
                ],
                actions: [],
                autoEvents: []
            })),
            
            initialState: {
                families: [
                { 
                    name: "Système Principal", 
                    type: "neutral", 
                    isActive: true, 
                    isToxic: false, 
                    influence: { stabilite: 0.5, bienEtre: 0.5 } 
                },
                { 
                    name: "Groupe Secondaire", 
                    type: "positive", 
                    isActive: true, 
                    isToxic: false, 
                    influence: { creativite: 0.6 } 
                }
                ],
                agentTemplates: [
                {
                    name: "Agent Test 1",
                    family: "Système Principal",
                    role: "observateur",
                    status: "neutre",
                    resources: { stabilite: 50, bienEtre: 50 },
                    theory: { primaryConcept: "Test" }
                }
                ],
                parameters: {
                stabilite: 50,
                creativite: 50,
                equite: 50,
                bienEtre: 50,
                apprentissage: 50,
                sante: 50,
                mediation: 50,
                emploi: 50
                },
                interactions: [],
                theory: { competingFrameworks: [] }
            },

            simulationEngine: {
                predictionModel: {
                enabled: true,
                method: 'bayesian',
                horizon: 15,
                storeHistory: true,
                counterfactuals: true
                },
                calibration: {
                enabled: true,
                mode: 'genetic',
                populationSize: 80,
                generations: 60,
                mutationRate: 0.15
                },
                logs: { store: true, granularity: 'tick' },
                dynamics: [
                { parameter: "bienEtre", formula: "pesa_nonlinear_threshold", periodicity: 1 },
                { parameter: "equite", formula: "pesa_basic_linear", periodicity: 1 }
                ],
                causality: [],
                computedMetrics: {},
                network: []
            },

            metrics: {
                stabilite: { start: 50, min: 0, max: 100 },
                creativite: { start: 50, min: 0, max: 100 },
                equite: { start: 50, min: 0, max: 100 },
                bienEtre: { start: 50, min: 0, max: 100 }
            },

            solutions: [],
            metadata: {
                versionEngine: '1.0-pesa-universel',
                standalone: true,
                hasProcessMining: true,
                hasCounterfactuals: true
            },
        },

        // ============================================================================
        // UI STATE
        // ============================================================================
        
        ui: {
          currentProtocol: 1,
          isNavOpen: true,
          isSaving: false,
          expandedSteps: {}
        },

        // ============================================================================
        // VALIDATION & ERREURS
        // ============================================================================
        
        errors: {
          crossProtocol: null,
          zod: {}
        },

        // ============================================================================
        // ACTIONS : MODE INTERFACE
        // ============================================================================
        
        toggleMode: () => set(state => ({
          interfaceMode: state.interfaceMode === 'simplifie' ? 'avance' : 'simplifie'
        })),
        
        setMode: (mode) => set({ interfaceMode: mode }),

        // ============================================================================
        // ACTIONS : FORMULAIRE
        // ============================================================================
        
        updateFormData: (path, value) => {
          set(state => {
            const newData = cloneDeep(state.formData);
            lodashSet(newData, path, value);
            return { formData: newData };
          });
        },
        
        loadTemplate: (templateData) => {
          set({
            formData: { ...templateData },
            ui: { ...get().ui, currentProtocol: 1 }
          });
          toast.success('Template chargé avec succès');
        },
        
        resetForm: () => {
          set({
            formData: get().getDefaultFormData(),
            ui: { currentProtocol: 1, isNavOpen: true, isSaving: false, expandedSteps: {} }
          });
          toast.info('Formulaire réinitialisé');
        },

        // ============================================================================
        // ACTIONS : NAVIGATION
        // ============================================================================
        
        setProtocol: (id) => set(state => ({
          ui: { ...state.ui, currentProtocol: id }
        })),
        
        nextProtocol: () => set(state => ({
          ui: {
            ...state.ui,
            currentProtocol: Math.min(state.ui.currentProtocol + 1, 5)
          }
        })),
        
        prevProtocol: () => set(state => ({
          ui: {
            ...state.ui,
            currentProtocol: Math.max(state.ui.currentProtocol - 1, 1)
          }
        })),
        
        toggleNav: () => set(state => ({
          ui: { ...state.ui, isNavOpen: !state.ui.isNavOpen }
        })),
        
        toggleStepExpanded: (index) => set(state => ({
          ui: {
            ...state.ui,
            expandedSteps: {
              ...state.ui.expandedSteps,
              [index]: !state.ui.expandedSteps[index]
            }
          }
        })),

        // ============================================================================
        // ACTIONS : LOGIQUE MÉTIER
        // ============================================================================
        
        // Générer les 7 steps par défaut
        generateDefaultSteps: () => {
          const defaultSteps = [
            {
              title: "Diagnostic Initial",
              description: "Observation du système dans son état stable.",
              problematisation: "Comment l'équilibre tient-il encore ?",
              epistemologicalQuestion: "Qu'est-ce qui rend cette stabilité fragile ?"
            },
            {
              title: "Montée des Tensions",
              description: "Prise de conscience des contradictions internes.",
              problematisation: "Quand la conscience devient-elle action ?",
              epistemologicalQuestion: "Le savoir suffit-il au changement ?"
            },
            {
              title: "Point d'Intervention",
              description: "Tentative coordonnée de rupture avec l'ordre.",
              problematisation: "La rupture peut-elle éviter l'absorption ?",
              epistemologicalQuestion: "Existe-t-il une science de la rupture ?"
            },
            {
              title: "Bascule Critique",
              description: "Moment irréversible ou retour brutal à l'ordre.",
              problematisation: "Où se situe le seuil de non-retour ?",
              epistemologicalQuestion: "La réalité change avant ou après ?"
            },
            {
              title: "Dernière Chance",
              description: "Ultime sursaut ou accélération de l'effondrement.",
              problematisation: "La fin est-elle évitable ?",
              epistemologicalQuestion: "Le temps est-il linéaire ou cyclique ?"
            },
            {
              title: "Résolution",
              description: "Conséquences : nouvel ordre ou chaos.",
              problematisation: "Quelle forme prend la stabilité émergente ?",
              epistemologicalQuestion: "Le nouveau diffère-t-il vraiment ?"
            },
            {
              title: "Bilan Système",
              description: "Analyse finale des mécanismes révélés.",
              problematisation: "Que reste-t-il après la résolution ?",
              epistemologicalQuestion: "Quelle connaissance survit ?"
            }
          ];
          
          set(state => ({
            formData: {
              ...state.formData,
              steps: defaultSteps.map(step => ({
                ...step,
                feedback: "",
                narrativeOverride: "",
                causality: [],
                phases: [
                  { type: "observation", duration: 15, description: "Observation" },
                  { type: "action", duration: 20, description: "Action" },
                  { type: "consequences", duration: 10, description: "Conséquences" }
                ],
                actions: [],
                autoEvents: []
              }))
            }
          }));
          
          toast.success('7 segments temporels générés');
        },
        
        // Générer solutions
        generateSolutions: async (tone, prompt, context) => {
          set(state => ({ ui: { ...state.ui, isSaving: true } }));
          
          try {
            const res = await api.get('/cases/generate-solutions', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ tone, prompt, context })
            });
            
            if (!res.ok) throw new Error(await res.text());
            
            const data = await res.json();
            
            if (data.solutions?.length > 0) {
              set(state => ({
                formData: {
                  ...state.formData,
                  solutions: data.solutions
                }
              }));
              
              toast.success('Bifurcations projetées avec succès');
              return data;
            } else {
              toast.warn('Aucune solution générée');
              return null;
            }
          } catch (err) {
            toast.error('Erreur génération : ' + err.message);
            return null;
          } finally {
            set(state => ({ ui: { ...state.ui, isSaving: false } }));
          }
        },
        
        // Sauvegarder et déployer
        deploySimulation: async (navigate) => {
          set(state => ({ ui: { ...state.ui, isSaving: true } }));
          
          try {
            const res = await api.get('/cases/create-standalone', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(get().formData)
            });
            
            if (!res.ok) throw new Error(await res.text());
            
            const { slug } = await res.json();
            toast.success('Simulation initialisée avec succès');
            
            setTimeout(() => {
              navigate(`/simulateur/generic-case/${slug}`);
            }, 1500);
          } catch (err) {
            toast.error('Échec déploiement : ' + err.message);
          } finally {
            set(state => ({ ui: { ...state.ui, isSaving: false } }));
          }
        },

        // ============================================================================
        // HELPERS
        // ============================================================================
        
        getDefaultFormData: () => ({
          slug: `sim-${Date.now().toString(36)}`,
          title: "Nouvelle Simulation",
          name: "Core",
          description: "Description à compléter",
          pedagogicalGoal: "Objectif pédagogique de la simulation",
          tags: [],
          thumbnail: "",
          difficulty: "moyen",
          estimatedTime: 25,
          isPublished: false,
          isLegacy: false,
          version: 7,
          
          steps: Array.from({ length: 7 }, (_, i) => ({
            title: [
              "Diagnostic Initial", 
              "Montée des Tensions", 
              "Point d'Intervention", 
              "Bascule Critique", 
              "Dernière Chance", 
              "Résolution", 
              "Bilan Système"
            ][i],
            description: "À compléter",
            problematisation: "",
            epistemologicalQuestion: "",
            feedback: "",
            narrativeOverride: "",
            causality: [],
            phases: [
              { type: "observation", duration: 15, description: "Phase initiale" },
              { type: "action", duration: 20, description: "Actions principales" },
              { type: "consequences", duration: 10, description: "Impacts" }
            ],
            actions: [],
            autoEvents: []
          })),
          
          initialState: {
            families: [
              { name: "Système Principal", type: "neutral", isActive: true, isToxic: false, influence: {} }
            ],
            parameters: {
              stabilite: 50,
              creativite: 50,
              equite: 50,
              bienEtre: 50,
              apprentissage: 50,
              sante: 50,
              mediation: 50,
              emploi: 50
            },
            agents: [],
            agentTemplates: [],
            theory: { competingFrameworks: [] },
            interactions: []
          },
          
          simulationEngine: {
            predictionModel: {
              enabled: true,
              method: 'bayesian',
              horizon: 15,
              counterfactuals: true
            },
            calibration: {
              enabled: true,
              mode: 'genetic',
              populationSize: 80,
              generations: 60,
              autoStop: true,
              mutationRate: 0.15,
              eliteSize: 12
            },
            logs: { store: true, granularity: 'tick' },
            processMining: {
              enabled: true,
              discoveryAlgorithm: 'heuristic',
              eventLog: []
            },
            scenarioEngine: { enabled: true, runs: 500, variants: [] },
            dynamics: [],
            causality: [],
            network: [],
            computedMetrics: {}
          },
          
          metrics: {
            stabilite: { start: 50, min: 0, max: 100, hidden: false },
            creativite: { start: 50, min: 0, max: 100, hidden: false },
            equite: { start: 50, min: 0, max: 100, hidden: false },
            bienEtre: { start: 50, min: 0, max: 100, hidden: false },
            apprentissage: { start: 50, min: 0, max: 100, hidden: false },
            sante: { start: 50, min: 0, max: 100, hidden: false },
            mediation: { start: 50, min: 0, max: 100, hidden: false },
            emploi: { start: 50, min: 0, max: 100, hidden: false }
          },
          
          solutions: [],
          
          metadata: {
            versionEngine: '1.0-pesa-universel',
            standalone: true,
            author: 'PESA Creator',
            license: 'CC-BY-NC-SA-4.0',
            createdAt: new Date(),
            hasProcessMining: true,
            hasAutoOptimization: true,
            hasCounterfactuals: true,
            hasDynamicDebrief: true,
            hasHiddenMetric: false,
            hasNarrativeOverride: true,
            hasOntologicalDebrief: false
          }
        })
      }),
      {
        name: 'pesa-case-creator-storage',
        partialize: (state) => ({
          formData: state.formData,
          interfaceMode: state.interfaceMode
        })
      }
    ),
    { name: 'PESA-CASE-CREATOR' }
  )
);